import { formatters } from '@seek/ca-myprofile-utils';
import { useHubble } from '@seek/hubble';
import { useTranslations } from '@vocab/react';
import {
  ButtonIcon,
  Text,
  Stack,
  Box,
  IconLocation,
  IconMail,
  Column,
  Columns,
  IconLink,
  TextLink,
  IconEdit,
  HiddenVisually,
} from 'braid-design-system';
import { useEffect } from 'react';

import { Card } from 'src/components/Card/Card';
import { CardError } from 'src/components/Card/CardError/CardError';
import { CardTitle } from 'src/components/Card/CardHeading/CardTitle/CardTitle';
import { FormDrawer } from 'src/components/FormDrawer/FormDrawer';
import { LoadingItem } from 'src/components/LoadingBlock/LoadingBlock';
import { CREATE_RESUME_PAGE, PERSONAL_DETAILS } from 'src/components/Router';
import { useNavigateRoute } from 'src/components/Router/useNavigateRoute';
import { usePage } from 'src/components/Router/usePage';
import { EDIT } from 'src/consts';
import { PERSONAL_DETAILS_FORM } from 'src/context/FormContext';
import { analyticsClient } from 'src/modules/analytics';
import { useAppConfig } from 'src/modules/appConfig';
import { timeToInteractive } from 'src/modules/performance-metrics';
import { useRetryAction } from 'src/modules/retry-actions/hooks';

import { setRouteAsTouched } from '../../CreateResume/actionCreators';
import { EditPersonalDetails } from '../EditPersonalDetails/EditPersonalDetails';
import type { usePersonalDetails, usePublicProfile } from '../data';

import translations from './.vocab';
import { InlineNudge } from './InlineNudge/InlineNudge';

export interface ReadPersonalDetailsProps {
  personalDetails: ReturnType<typeof usePersonalDetails>['personalDetails'];
  personalDetailsLoading: boolean;
  isPersonalDetailsError: boolean;
  publicProfileData: ReturnType<typeof usePublicProfile>['data'];
  publicProfileLoading: boolean;
  isPublicProfileError: boolean;
}

export const ReadPersonalDetails = (props: ReadPersonalDetailsProps) => {
  useEffect(() => {
    timeToInteractive();
  }, []);
  const { t } = useTranslations(translations);
  const { zone, language } = useAppConfig();
  const title = t('Edit personal details');
  const page = usePage();
  const navigate = useNavigateRoute();

  const {
    personalDetails,
    personalDetailsLoading,
    isPersonalDetailsError,
    publicProfileData,
    publicProfileLoading,
    isPublicProfileError,
  } = props;

  const firstName = personalDetails?.personalDetails.firstName || '';
  const lastName = personalDetails?.personalDetails.lastName || '';
  const name = formatters.formatName(firstName, lastName);
  const emailAddress = personalDetails?.emailAddress;
  const location = personalDetails?.currentLocation2?.shortDescription || '';

  const { publicProfile } = publicProfileData?.viewer || {};

  const isCardLoading = Boolean(
    personalDetailsLoading || isPersonalDetailsError,
  );

  const retryAction = useRetryAction(PERSONAL_DETAILS);
  const showErrorAlert = Boolean(retryAction?.canRetry);

  const hubble = useHubble();

  const trackProfileUrlClick = () => {
    if (publicProfile?.url) {
      hubble.trackEvent('component_pressed', {
        currentPageSection: 'profile header',
        component: {
          type: 'textLink',
          name: 'public profile linkout',
        },
        displayLanguage: language,
        zone,
      });
    }
  };

  const renderPublicProfile = () => {
    if (publicProfileLoading || isPublicProfileError) {
      return (
        <LoadingItem
          variant="transparent"
          type="text"
          size="standard"
          width="medium"
        />
      );
    }

    if (publicProfile?.url) {
      return (
        <Text
          maxLines={1}
          size="small"
          icon={<IconLink tone="secondary" />}
          data={{
            'hj-masked': 'true',
            automation: 'personal-details-public-profile',
          }}
        >
          <TextLink
            href={publicProfile.url}
            hitArea="standard"
            weight="weak"
            onClick={trackProfileUrlClick}
            data={{ testid: 'personal-details-public-profile-link' }}
            target="_blank"
          >
            {publicProfile.displayUrl}
          </TextLink>
        </Text>
      );
    }

    return null;
  };

  const renderNameSection = () => (
    <Columns space="medium" alignY="center">
      <Column>
        <InlineNudge
          type="Heading"
          level="2"
          component="h2"
          nudgeText={t('Add your name')}
          fieldToFocus="first-name"
          displayText={name}
          data={{ automation: 'inline-nudge-name' }}
        />
      </Column>
      <Column width="content">
        <Box
          paddingRight={{
            mobile: 'none',
            tablet: 'none',
            desktop: 'gutter',
          }}
        >
          <ButtonIcon
            icon={<IconEdit />}
            variant="transparent"
            label={t('Edit personal details')}
            data={{ automation: 'personal-details-edit-pencil' }}
            onClick={() => {
              analyticsClient.cardOpened({
                currentPageSection: 'personal details',
                currentPageSectionState: 'edit',
              });

              if (page === CREATE_RESUME_PAGE) {
                setRouteAsTouched(PERSONAL_DETAILS);
              }

              // Navigate to edit mode to open the drawer
              navigate({
                card: PERSONAL_DETAILS,
                mode: EDIT,
              });
            }}
          />
        </Box>
      </Column>
    </Columns>
  );

  return (
    <>
      <Card
        name={PERSONAL_DETAILS}
        loading={isCardLoading}
        loader={
          <Stack space="large">
            <LoadingItem
              variant="standard"
              type="heading"
              level="1"
              width="large"
            />
            <Stack space="small">
              <LoadingItem
                variant="standard"
                type="text"
                size="standard"
                width="small"
              />
              <LoadingItem
                variant="standard"
                type="text"
                size="standard"
                width="small"
              />
            </Stack>
          </Stack>
        }
      >
        <HiddenVisually>
          <CardTitle name={PERSONAL_DETAILS} title={t('Personal details')} />
        </HiddenVisually>
        <Stack space="large">
          {showErrorAlert ? <CardError card={PERSONAL_DETAILS} /> : null}
          {renderNameSection()}
          <Stack space="small">
            <InlineNudge
              type="Text"
              size="small"
              nudgeText={t('Add a home location')}
              fieldToFocus="current-location"
              displayText={location}
              icon={<IconLocation tone="secondary" />}
              data={{ automation: 'inline-nudge-location' }}
            />
            <Text
              maxLines={1}
              size="small"
              icon={<IconMail tone="secondary" />}
              data={{
                'hj-masked': 'true',
                automation: 'personal-detail-email',
              }}
            >
              {emailAddress}
            </Text>
            {renderPublicProfile()}
          </Stack>
        </Stack>
      </Card>
      <FormDrawer
        title={title}
        component={EditPersonalDetails}
        card={PERSONAL_DETAILS}
        formName={PERSONAL_DETAILS_FORM}
      />
    </>
  );
};
